import React from 'react';
import { cn } from '~/lib/utils';
import styles from './ComposerAttachmentTray.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface ComposerAttachmentTrayProps {
  /**
   * The chips. `AttachmentCard`s in every artboard — titled (`Chat/Attachment`, `Chat/Attachments`,
   * `Founder`, `Engineer`) or image (`Chat/Images`) — and the tray does not care which: it lays out
   * 96px squares 8px apart and clips whatever runs past its edge.
   */
  children: React.ReactNode;
  /**
   * Accessible name for the scroll region. The tray is a real tab stop (see below), and a tab stop
   * with no name is announced as "group" and nothing else, so it carries one — defaulted rather
   * than required because §1.28's prop surface is `{ children; theme? }` and every artboard holds
   * the same thing: the files you attached.
   */
  label?: string;
  /**
   * The composer around this tray is disabled (`Chat/Disabled`).
   *
   * Paints nothing. The artboard dims the whole card at `opacity: 0.45` and leaves every child
   * exactly as at rest, so a disabled tray is pixel-identical to the tray above it — this is a
   * focusability and pointer change only, and the stylesheet has no rule for it.
   *
   * What it changes is that nothing in here is reachable any more. Two things have to go, and they
   * are reached two different ways: the region's own tab stop, which is a `tabIndex` this file
   * owns, and the remove dots, which are `<button>`s inside `AttachmentCard`s that arrive through
   * `children` — this component never renders them and cannot pass them a prop. `AttachmentCard`
   * has no `disabled` prop to pass either, so the only mechanism that reaches both is `inert` on
   * the root. See `INERT` below.
   */
  disabled?: boolean;
  theme?: ChatTheme;
  className?: string;
}

/**
 * What makes a disabled tray unreachable rather than merely unclickable.
 *
 * `inert` takes focus, the pointer and the accessibility tree for the whole subtree at once, which
 * is the only way this component can disable controls it does not own — the remove dots belong to
 * the `AttachmentCard`s handed in as `children`. `pointer-events: none` in CSS would have taken the
 * pointer and left every dot on a 45%-dimmed card still Tab-reachable and still firing, which is
 * the exact hole this prop was added to close. `Composer` disables its whole card the same way and
 * for the same reason.
 *
 * The cost is real and accepted: an inert tray cannot be read by a screen reader either, only
 * skipped. That is the right trade — a focusable control that does nothing is a WCAG failure, an
 * unreadable disabled region is not, and it matches what a disabled `Composer` already does to
 * everything inside it.
 *
 * Spread rather than written as a JSX attribute because React 18's DOM typings predate `inert`.
 * The empty string is deliberate: React 18 forwards an unknown attribute only when its value is a
 * string, and `inert=""` is the attribute's own present form in the HTML.
 */
const INERT = { inert: '' };

/**
 * The strip of attachment chips that docks **inside** the composer card, above the text row.
 *
 * One of three attachment containers in this design, and the only one that is a fixed-height
 * scroller. Its siblings are `MessageAttachmentRow` (above a sent bubble, no height, no clip) and
 * `MessageImageGrid` (a wrapping 304px grid). They look alike in a screenshot and behave nothing
 * alike, which is why they are three components: this one has to hold the composer's height stable
 * while the number of attachments changes, so it is 110px whether it holds one chip or ten.
 *
 * The overflow is the point rather than an edge case. The content box is `400 − 14 − 14 = 372`, so
 * three chips (304) sit inside it and a fourth (408) does not — `Chat/Images` is drawn with four
 * for exactly that reason, and `Chat/Attachments` captions it "the row scrolls".
 *
 * Because the overflow is stated behaviour, reaching it has to be too. Chromium and WebKit do not
 * make a scroll container keyboard-focusable on their own (only Firefox does), and the 110px height
 * is `14 + 96` with no gutter to spare for a classic scrollbar — so the tray declares itself a
 * named, focusable region instead. That is what makes the arrow keys, and the `:focus-visible`
 * ring in the stylesheet, work outside Firefox.
 */
export function ComposerAttachmentTray({
  children,
  label = 'Attachments',
  disabled = false,
  theme: themeProp,
  className,
}: ComposerAttachmentTrayProps) {
  const theme = useV15Theme(themeProp);
  return (
    <div
      className={cn(chatThemeClass(theme), styles.tray, className)}
      // A scroll region the user must be able to reach without a horizontal wheel. `group` rather
      // than `region` deliberately: this is a handful of chips inside the composer, not a landmark
      // worth an entry in a screen reader's region list.
      role="group"
      aria-label={label}
      // The rule reads `group` as purely structural and objects to focusing it. A scrollable box is
      // the case it does not model: WCAG wants scroll containers reachable by keyboard, and here
      // there is no other route in — the chips' remove dots are optional (`AttachmentCard` renders
      // an inert `aria-hidden` span without `onRemove`), so a tray of sent cards has no focusable
      // descendant, and the scrollbar is suppressed because 110px is 14 + 96 with no gutter to
      // spare. `v1_5/Layout/Seam` disables the same rule for the same reason.
      //
      // -1 once disabled rather than dropping the attribute: the region has to leave the tab
      // order, but it should stay programmatically focusable so a caller that had focus in here
      // can move it out deliberately. `INERT` is what closes the rest — the arrow keys, the
      // pointer and the remove dots inside the chips; this is only what closes Tab.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={disabled ? -1 : 0}
      {...(disabled ? INERT : null)}
    >
      {children}
    </div>
  );
}
